import { FC } from 'react'
import { Text, Box, Flex, Anchor, Button } from '../primitives'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTwitter, faDiscord, faFacebook, faTelegram, faInstagram, } from '@fortawesome/free-brands-svg-icons'
import Link from 'next/link'
import Image from 'next/image'
import { useTheme } from 'next-themes'
import { useMarketplaceChain } from '@/hooks'
import { faEnvelopeOpen, faGlobe, faHome, faLocation, faMailBulk, faPhone } from '@fortawesome/free-solid-svg-icons'

type SectionTitleProps = {
  title: string
}

const SectionTitle: FC<SectionTitleProps> = ({ title }) => (
  <Text style="subtitle1" css={{ color: '$gray12', mb: 8, fontSize: 'large' }}>
    {title}
  </Text>
)

type SectionLinkProps = {
  name: string
  href: string
}

const SectionLink: FC<SectionLinkProps> = ({ name, href }) => (
  <Anchor
    target="_blank"
    rel="noopener noreferrer"
    href={href}
    weight="medium"
    css={{ fontSize: 14, mt: 16 }}
  >
    {name}
  </Anchor>
)

const developerSectionLinks = [
  {
    name: 'Home',
    href: '/',
  },
  {
    name: 'Privacy Policy',
    href: '/privacypolicy',
  },
  {
    name: 'Terms and Service',
    href: '/teamsconditions',
  },
  {
    name: 'Disclaimer',
    href: '/disclaimer',
  }
]

const companySectionLinks = [
  {
    name: 'About Us',
    href: '/aboutus',
  },
  {
    name: 'Referral Program',
    href: '/referralProgram',
  },
  {
    name: 'Blog',
    href: '/',
  },
  {
    name: 'Contact Us',
    href: '/contactus',
  },
  {
    name: 'FAQ',
    href: '/faq',
  },
]

export const Footer = () => {
  const { theme } = useTheme()
  const { routePrefix } = useMarketplaceChain()

  return (
    <Box css={{ backgroundColor: '$blackA5' }} className='wow fadeInUp relative z-10 bg-opacity-5 pt-16 md:pt-20 lg:pt-24 ' data-wow-delay=".1s">
      <Flex
        justify="between"
        css={{
          borderTop: '1px solid $gray7',
          borderStyle: 'solid',
          p: 24,
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 36,
          '@bp600': {
            flexDirection: 'row',
            gap: 0,
          },
        }}
      >
        <Flex
          direction="column"
          css={{ alignItems: 'flex-start', '@bp600': { alignItems: 'flex-start' } }}
        >
          <Link href={`/${routePrefix}`}>
            <Box css={{ width: 112, cursor: 'pointer' }}>
              {theme == 'dark' ? (
                <Image
                  src="/coinpulseNftMarketLogo.png"
                  width={112}
                  height={36}
                  alt="Coinpulse Online Financial Service"
                />
              ) : (
                <Image
                  src="/coinpulseNftMarketLogoLightFooter.png?q=dark"
                  width={112}
                  height={36}
                  alt="Coinpulse Online Financial Service"
                />
              )}
            </Box>
          </Link>
          <Flex css={{ gap: '$4', mt: 16 }}>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://twitter.com/coinpulsepro"
            >
              <Button size="xs" color="gray3">
                <FontAwesomeIcon icon={faTwitter} width={14} height={14} />
              </Button>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://discord.gg/MAzfWTEZtt"
            >
              <Button size="xs" color="gray3">
                <FontAwesomeIcon icon={faDiscord} width={14} height={14} />
              </Button>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://facebook.com/coinpulse101"
            >
              <Button size="xs" color="gray3">
                <FontAwesomeIcon icon={faFacebook} width={14} height={14} />
              </Button>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://telegram.me/coinpulse101"
            >
              <Button size="xs" color="gray3">
                <FontAwesomeIcon icon={faTelegram} width={14} height={14} />
              </Button>
            </a>
          </Flex>
          <Flex direction="row" className='max-w-md'>
            <p>Welcome to Coinpulse Online Financial Service, the inveterate platform designed for NFT lovers and crypto enthusiasts alike. Our platform offers a seamless and secure environment to buy and sell NFTs, as well as exclusive investment plans for individuals seeking to grow their crypto assets. </p>

          </Flex>
        </Flex>
        <Flex css={{ gap: 80, '@bp600': { gap: 136 } }}>
          <Box className='grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-2 lg:grid-cols-3'>
            <Flex direction="column">
              <SectionTitle title="Usefull Links" />
              {developerSectionLinks.map((props) => (
                <SectionLink key={props.name} {...props} />
              ))}
            </Flex>
            <Flex direction="column">
              <SectionTitle title="Important Link" />
              {companySectionLinks.map((props) => (
                <SectionLink key={props.name} {...props} />
              ))}
            </Flex>
            <Flex direction="column">
              <SectionTitle title="Contact Info" />
              <Flex direction="column" className='mb-3 flex items-start text-left flex-wrap '>
                <Text css={{ color: 'gray3' }} className='text-xs float-left pb-4'>
                  <FontAwesomeIcon icon={faEnvelopeOpen} width={34} height={34} />
                  <Text className="text-l lg:text-xs xl:text-sm font-medium leading-normal mb-4">support@coinpulse.online</Text>
                </Text>
                <Text css={{ color: 'gray3' }} className='text-xs float-left pb-4'>
                  <FontAwesomeIcon icon={faGlobe} width={34} height={34} />
                  <Text className="text-l lg:text-xs xl:text-sm font-medium leading-normal mb-4">www.coinpulse.online</Text>
                </Text>
                <Text css={{ color: 'gray3' }} className='text-xs float-left pb-4'>
                  <FontAwesomeIcon icon={faPhone} width={34} height={34} />
                  <Text className="text-l lg:text-xs xl:text-sm font-medium leading-normal mb-4">+44 7572 120508</Text>
                </Text>
                <Text css={{ color: 'gray3' }} className='text-xs float-left pb-4'>
                  <FontAwesomeIcon icon={faHome} width={34} height={34} />
                  <Text className="text-l lg:text-xs xl:text-sm font-medium leading-normal mb-4">1 Principal Pl, London EC2A 2FA</Text>
                </Text>
                <Text css={{ color: 'gray3' }} className='text-xs float-left pb-4'>
                  <FontAwesomeIcon icon={faLocation} width={34} height={34} />
                  <Text className="text-l lg:text-xs xl:text-sm font-medium leading-normal mb-4">United Kingdom</Text>
                </Text>
              </Flex>
            </Flex>
          </Box>
        </Flex>

      </Flex>
      <Box css={{ p: '$4', backgroundColor: '$gray1' }}><Box className="container"><p className="text-center text-base text-body-color">© 2023 {' '} <a href="/" rel="nofollow noopener">Coinpulse Online Financial Service {' '}</a>All Rights Reserved.</p></Box></Box>
      <Box className="absolute right-0 top-14 z-[-1]"><svg width="55" height="99" viewBox="0 0 55 99" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.8" cx="49.5" cy="49.5" r="49.5" fill="#959CB1"></circle><mask id="mask0_94:899" maskUnits="userSpaceOnUse" x="0" y="0" width="99" height="99"><circle opacity="0.8" cx="49.5" cy="49.5" r="49.5" fill="#4A6CF7"></circle></mask><g mask="url(#mask0_94:899)"><circle opacity="0.8" cx="49.5" cy="49.5" r="49.5" fill="url(#paint0_radial_94:899)"></circle><g opacity="0.8" filter="url(#filter0_f_94:899)"><circle cx="53.8676" cy="26.2061" r="20.3824" fill="white"></circle></g></g><defs><filter id="filter0_f_94:899" x="12.4852" y="-15.1763" width="82.7646" height="82.7646" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend><feGaussianBlur stdDeviation="10.5" result="effect1_foregroundBlur_94:899"></feGaussianBlur></filter><radialGradient id="paint0_radial_94:899" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(49.5 49.5) rotate(90) scale(53.1397)"><stop stopOpacity="0.47"></stop><stop offset="1" stopOpacity="0"></stop></radialGradient></defs></svg></Box>
      <Box className="absolute left-0 bottom-24 z-[-1]"><svg width="79" height="94" viewBox="0 0 79 94" fill="none" xmlns="http://www.w3.org/2000/svg"><rect opacity="0.3" x="-41" y="26.9426" width="66.6675" height="66.6675" transform="rotate(-22.9007 -41 26.9426)" fill="url(#paint0_linear_94:889)"></rect><rect x="-41" y="26.9426" width="66.6675" height="66.6675" transform="rotate(-22.9007 -41 26.9426)" stroke="url(#paint1_linear_94:889)" strokeWidth="0.7"></rect><path opacity="0.3" d="M50.5215 7.42229L20.325 1.14771L46.2077 62.3249L77.1885 68.2073L50.5215 7.42229Z" fill="url(#paint2_linear_94:889)"></path><path d="M50.5215 7.42229L20.325 1.14771L46.2077 62.3249L76.7963 68.2073L50.5215 7.42229Z" stroke="url(#paint3_linear_94:889)" strokeWidth="0.7"></path><path opacity="0.3" d="M17.9721 93.3057L-14.9695 88.2076L46.2077 62.325L77.1885 68.2074L17.9721 93.3057Z" fill="url(#paint4_linear_94:889)"></path><path d="M17.972 93.3057L-14.1852 88.2076L46.2077 62.325L77.1884 68.2074L17.972 93.3057Z" stroke="url(#paint5_linear_94:889)" strokeWidth="0.7"></path><defs><linearGradient id="paint0_linear_94:889" x1="-41" y1="21.8445" x2="36.9671" y2="59.8878" gradientUnits="userSpaceOnUse"><stop stopColor="#4A6CF7" stopOpacity="0.62"></stop><stop offset="1" stopColor="#4A6CF7" stopOpacity="0"></stop></linearGradient><linearGradient id="paint1_linear_94:889" x1="25.6675" y1="95.9631" x2="-42.9608" y2="20.668" gradientUnits="userSpaceOnUse"><stop stopColor="#4A6CF7" stopOpacity="0"></stop><stop offset="1" stopColor="#4A6CF7" stopOpacity="0.51"></stop></linearGradient><linearGradient id="paint2_linear_94:889" x1="20.325" y1="-3.98039" x2="90.6248" y2="25.1062" gradientUnits="userSpaceOnUse"><stop stopColor="#4A6CF7" stopOpacity="0.62"></stop><stop offset="1" stopColor="#4A6CF7" stopOpacity="0"></stop></linearGradient><linearGradient id="paint3_linear_94:889" x1="18.3642" y1="-1.59742" x2="113.9" y2="80.6826" gradientUnits="userSpaceOnUse"><stop stopColor="#4A6CF7" stopOpacity="0"></stop><stop offset="1" stopColor="#4A6CF7" stopOpacity="0.51"></stop></linearGradient><linearGradient id="paint4_linear_94:889" x1="61.1098" y1="62.3249" x2="-8.82468" y2="58.2156" gradientUnits="userSpaceOnUse"><stop stopColor="#4A6CF7" stopOpacity="0.62"></stop><stop offset="1" stopColor="#4A6CF7" stopOpacity="0"></stop></linearGradient><linearGradient id="paint5_linear_94:889" x1="65.4236" y1="65.0701" x2="24.0178" y2="41.6598" gradientUnits="userSpaceOnUse"><stop stopColor="#4A6CF7" stopOpacity="0"></stop><stop offset="1" stopColor="#4A6CF7" stopOpacity="0.51"></stop></linearGradient></defs></svg></Box>
      <Flex className='fixed bottom-0 shadow-2xl right-0'>
        <Box className="flex-1 pr-4 justify-center items-center content-center text-center">
          <Link href="https://t.me/coinpulse101">
            <Box className="uppercase font-bold text-xl shadow-lg rounded-full bg-orange-500 h-12 w-12 animate-bounce">
              <Text className='text-5xl'>
                <FontAwesomeIcon icon={faTelegram} />
              </Text>
            </Box>
          </Link>
        </Box>
      </Flex>
    </Box>
  )
}


export const CommunityLink = () => {
  const fiveTemplateColumns = '1fr 1fr 1fr 1fr 1fr'
  return (
    <footer className="lg:hidden xl:hidden flex w-full fixed bottom-0 z-30 shadow-2xl">

      <Box css={{ backgroundColor: '$gray2', left: '0', right: '0', display: 'flex' }} className="w-full mx-auto p-2 justify-center">
        <Box className="flex items-center justify-center  w-full">
          <Box className="flex-1 pr-4 justify-center items-center content-center text-center">
            <Link href="https://www.instagram.com/j0sephalma">
              <Box className="uppercase font-bold text-xl">
                <FontAwesomeIcon icon={faInstagram} width={16} height={16} />
              </Box>
            </Link>
          </Box>
          <Box className="flex-1 pr-4 justify-center items-center content-center text-center">
            <Link href="https://twitter.com/coinpulsepro">
              <Box className="uppercase font-bold text-xl">
                <FontAwesomeIcon icon={faTwitter} width={16} height={16} />
              </Box>
            </Link>
          </Box>
          <Box className="flex-1 pr-4 justify-center items-center content-center text-center animate-bounce">
            <Link href="https://t.me/coinpulse_global">
              <Box className="uppercase font-bold text-xl  shadow-lg rounded-full bg-orange-500 h-12 w-12">
                <Text className='text-5xl'>
                  <FontAwesomeIcon icon={faTelegram} />
                </Text>
              </Box>
            </Link>
          </Box>
          <Box className="flex-1 pr-4 justify-center items-center content-center text-center">
            <Link href="https://www.facebook.com/coinpulse101">
              <Box className="uppercase font-bold text-xl ">
                <FontAwesomeIcon icon={faFacebook} width={16} height={16} />
              </Box>
            </Link>
          </Box>
          <Box className="flex-1 pr-4 justify-center items-center content-center text-center">
            <Link href="https://discord.com/invite/MAzfWTEZtt">
              <Box className="uppercase font-bold text-xl">
                <FontAwesomeIcon icon={faDiscord} width={16} height={16} />
              </Box>
            </Link>
          </Box>
        </Box>
      </Box>
    </footer>
  )
}